<template>
  <div
    id="all_expenses"
    :class="{
      'contentBlock marginBottomSmall': isDisabled,
      contentBlock: !isDisabled,
    }"
    role="form"
    aria-label="Expenses Form"
  >
    <!-- Advertising Expenses -->
    <div class="contentBlock marginBottomSmaller">
      <ExpenseInput
        :expense="advertisingExpense"
        :is-disabled="isDisabled"
        @update="handleExpenseUpdate"
      />
    </div>

    <!-- Moving Expenses -->
    <div class="contentBlock marginBottomSmaller">
      <ExpenseInput
        :expense="movingExpense"
        :is-disabled="isDisabled"
        @update="handleExpenseUpdate"
      />
    </div>

    <!-- Setup Expenses -->
    <div class="contentBlock marginBottomSmaller">
      <ExpenseInput
        :expense="setupExpense"
        :is-disabled="isDisabled"
        @update="handleExpenseUpdate"
      />
    </div>

    <!-- GIA Expenses -->
    <div class="contentBlock marginBottomSmaller">
      <ExpenseInput
        :expense="giaExpense"
        :is-disabled="isDisabled"
        @update="handleExpenseUpdate"
      />
    </div>

    <!-- Custom Expenses -->
    <div
      v-for="expense in customExpenses"
      :key="expense.id"
      class="contentBlock marginBottomSmaller"
    >
      <ExpenseInput
        :expense="expense"
        :is-disabled="isDisabled"
        @update="handleCustomExpenseUpdate"
      />
    </div>
  </div>
</template>

<script>
  import contractState from '@/mixins/contractState'
  import { mapMutations } from 'vuex'
  import { UPDATE_EXPENSES } from '@/store/contract/constants'
  import ExpenseInput from '@/components/Contract/components/ExpenseInput.vue'

  export default {
    name: 'Expenses',
    components: {
      ExpenseInput,
    },
    mixins: [contractState],
    props: {
      contract: {
        type: Object,
        required: true,
      },
    },
    data () {
      const findExpense = type => this.contract?.expenses?.find(e => e.type === type) || {}
      const customExpenses = this.contract?.expenses?.filter(e => e.type === 'other') || []
      return {
        pendingUpdates: {},
        advertisingExpense: {
          amount: '',
          ...findExpense('advertising'),
          description: 'Advertising Expenses',
          type: 'advertising',
          contract_id: this.contract?.id,
        },
        movingExpense: {
          amount: '',
          ...findExpense('moving'),
          description: 'Moving Expenses',
          type: 'moving',
          contract_id: this.contract?.id,
        },
        setupExpense: {
          amount: '',
          ...findExpense('setup'),
          description: 'Setup Expenses',
          type: 'setup',
          contract_id: this.contract?.id,
        },
        giaExpense: {
          amount: '',
          ...findExpense('gia'),
          description: 'Gemstone ID and Grading Expenses',
          type: 'gia',
          contract_id: this.contract?.id,
        },
        customExpenses: customExpenses.map(expense => ({
          ...expense,
          contract_id: this.contract?.id,
        })),
      }
    },
    computed: {
      /**
       * Whether the expenses are disabled
       * @returns {boolean} True if the expenses are disabled, false otherwise
       */
      isDisabled () {
        return !this.isStaff || !this.isEditable()
      },
      // Remove the visibleCustomExpenses computed property
    },
    mounted () {
      if (!this.isDisabled) {
        this.addCustomExpense()
      }
    },
    methods: {
      ...mapMutations({
        mutateExpenses: UPDATE_EXPENSES,
        setContractData: 'SET_CONTRACT_DATA',
      }),
      /**
       * Handle expense update
       * @param {Object} updatedExpense - The updated expense
       */
      handleExpenseUpdate (updatedExpense) {
        // Add to pending updates
        this.pendingUpdates[updatedExpense.type] = updatedExpense

        // Update local state based on expense type
        switch (updatedExpense.type) {
          case 'advertising':
            this.advertisingExpense = { ...updatedExpense }
            break
          case 'moving':
            this.movingExpense = { ...updatedExpense }
            break
          case 'setup':
            this.setupExpense = { ...updatedExpense }
            break
          case 'gia':
            this.giaExpense = { ...updatedExpense }
            break
        }

        this.updateExpenses(this.pendingUpdates)
        this.pendingUpdates = {} // Clear pending updates
      },
      /**
       * Add a new custom expense
       */
      addCustomExpense () {
        this.customExpenses.push({
          id: Date.now(), // Temporary ID until saved
          contract_id: this.contract.id,
          description: '',
          amount: '',
          type: 'other',
        })
      },
      /**
       * Get all custom expenses that have values
       * @returns {Object[]} Array of custom expenses that have values
       */
      getAllCustomExpenses () {
        // Filter out any custom expenses where the description is empty
        return this.customExpenses.filter(
          expense => expense.description && expense.description.trim() !== '',
        )
      },
      /**
       * Handle updates to custom expenses
       */
      handleCustomExpenseUpdate (updatedExpense) {
        // Add to pending updates using the ID as the key
        this.pendingUpdates[`custom-${updatedExpense.id}`] = updatedExpense

        // Update the local custom expenses array
        const index = this.customExpenses.findIndex(e => e.id === updatedExpense.id)
        if (index >= 0) {
          this.$set(this.customExpenses, index, updatedExpense)
        }

        // console.log('Updating custom expenses:', this.pendingUpdates); // Optional: for debugging
        this.updateExpenses(this.pendingUpdates)
        this.pendingUpdates = {} // Clear pending updates
        this.checkExpenseInputsForValues()
      },
      /**
       * Check if all custom expenses have values.
       * If so, add a new expense.
       * If not all are filled, ensure only one blank input row exists.
       */
      checkExpenseInputsForValues () {
        const activeElementId = document.activeElement?.id

        const allCompletelyFilled = this.customExpenses.every(
          expense => expense.description.trim() !== '' && expense.amount.trim() !== '',
        )

        if (allCompletelyFilled) {
          // All existing rows are completely filled, so add a new blank one for input.
          this.addCustomExpense()
          this.$nextTick(() => {
            if (activeElementId) {
              // Attempt to restore focus, though the new row might get focus by default.
              document.getElementById(activeElementId)?.focus()
            }
          })
        } else {
          // Not all rows are completely filled.
          // We need to ensure there's at most one completely blank row.
          const filledOrPartiallyFilledRows = []
          const blankRows = []

          this.customExpenses.forEach(expense => {
            if (expense.description.trim() === '' && expense.amount.trim() === '') {
              blankRows.push(expense)
            } else {
              filledOrPartiallyFilledRows.push(expense)
            }
          })

          if (blankRows.length > 1) {
            // More than one blank row exists. Remove extras.
            // Keep all filled/partially filled rows and only the first blank row.
            this.customExpenses = [...filledOrPartiallyFilledRows, blankRows[0]]
            // Vue's reactivity will handle the update.
            // Attempt to restore focus.
            this.$nextTick(() => {
              if (activeElementId) {
                const elementToFocus = document.getElementById(activeElementId)
                if (elementToFocus) {
                  elementToFocus.focus()
                }
                // If the focused element was in a removed row, focus might be lost.
                // Further refinement could focus the remaining blank row or last input.
              }
            })
          }
          // If blankRows.length is 0 or 1, and not allCompletelyFilled,
          // the state is fine (either one blank row, or all rows have some content).
          // No need to add a blank row here as `addCustomExpense` is only for when all are *completely* filled.
        }
      },
      /**
       * Update the expenses in the contract
       * @param {Object} update - The update to apply to the expenses
       */
      updateExpenses (update) {
        try {
          // Start with current contract expenses or empty array
          let expenses = []

          // Add standard expenses that have values
          ;[
            this.advertisingExpense,
            this.movingExpense,
            this.setupExpense,
            this.giaExpense,
          ].forEach(stdExpense => {
            if (stdExpense.amount != null && String(stdExpense.amount).trim() !== '') {
              expenses.push({
                ...stdExpense,
                contract_id: this.contract?.id,
              })
            }
          })

          // Add custom expenses that have values
          // getAllCustomExpenses filters the raw this.customExpenses list
          const validCustomExpenses = this.getAllCustomExpenses()
          validCustomExpenses.forEach(expense => {
            const pendingUpdate = update[`custom-${expense.id}`]
            expenses.push({
              ...expense,
              ...(pendingUpdate || {}), // This spread might be redundant if 'expense' is already the latest
              contract_id: this.contract?.id,
            })
          })

          this.mutateExpenses(expenses)

          // Update contract in store with new expenses
          const updatedContract = {
            ...this.contract,
            expenses,
          }
          this.setContractData(updatedContract)
        } catch (error) {
          console.error('Failed to update expenses:', error)
        }
      },
    },
  }
</script>

<style scoped></style>
